import {
  colorTokensMatch,
  effectiveAccessoryMode,
  inferWoodFromText,
  isWoodMaterialToken,
  normalizeSize,
  normalizeToken,
  pickOptionForSize,
  resolveLinkedProductHandle,
  variantColorToToken,
} from "./normalize";
import type {
  BeraterAnswer,
  BeraterData,
  DesiredAccessory,
  DesiredSelection,
  OptionKind,
  ResolveOutcome,
  ResolvedItem,
  ShopifyJsProduct,
  ShopifyJsVariant,
} from "./types";

/** A variant may be sold AS the bed only if its Variante value names a complete bed
 *  (or is the explicit "ohne Zubehör" base). Accessory-only variants like
 *  "Rausfallschutz" / "Bettkasten / Schublade" (Etagenbett) must never win. */
const BED_NOUNS = [
  "babybett",
  "bodenbett",
  "kinderbett",
  "hausbett",
  "panorama",
  "etagenbett",
  "jugendbett",
  "familienbett",
  "einzelbett",
  "doppelbett",
];

/** Selected bed variant must cost at least this share of the product's most
 *  expensive variant — second net against accessory-only variants. */
const PRICE_SANITY_RATIO = 0.5;

export const classifyOptionName = (raw: string): OptionKind => {
  const n = raw.toLowerCase(); // "Gröẞe" → "größe" (ẞ→ß), covers "Bettgröße" via includes
  if (n.includes("größe") || n.includes("grösse") || n.includes("size")) return "size";
  if (n.includes("farbe") || n.includes("muster")) return "color";
  if (n.includes("variante")) return n.includes("matratze") ? "material" : "variante";
  if (n === "material") return "material";
  return "other"; // Höhe, Einteilung, Title, …
};

const optionName = (option: string | { name: string }): string => (typeof option === "string" ? option : option.name);

const variantOptionValue = (variant: ShopifyJsVariant, index: number): string =>
  variant.options?.[index] ?? [variant.option1, variant.option2, variant.option3][index] ?? "";

const isCompleteBed = (varianteValue: string): boolean => {
  const n = normalizeToken(varianteValue);
  if (n.includes("ohne")) return true;
  return BED_NOUNS.some((noun) => n.startsWith(noun));
};

const matchesAccessoryToken = (varianteValue: string, token: string): boolean =>
  normalizeToken(varianteValue).includes(normalizeToken(token));

const isWoodMaterialOption = (raw: string): boolean => isWoodMaterialToken(raw);

/** Derives the desired selection (variant dimensions + accessories) from the answers. */
export const buildDesiredSelection = (data: BeraterData, answers: BeraterAnswer[]): DesiredSelection => {
  const desired: DesiredSelection = { size: null, color: null, wood: null, material: null, accessories: [] };
  const stepsByHandle = new Map(data.steps.map((step) => [step.handle, step]));

  for (const answer of answers) {
    const step = stepsByHandle.get(answer.stepHandle);
    if (!step || !answer.values.length) continue;

    switch (step.attribute) {
      case "size":
        desired.size = answer.values[0];
        break;
      case "color":
        desired.color = answer.values[0];
        break;
      case "wood":
        desired.wood = answer.values[0];
        break;
      case "material":
        desired.material = answer.values[0];
        break;
      case "accessories":
        break;
      default:
        break;
    }
  }

  for (const answer of answers) {
    const step = stepsByHandle.get(answer.stepHandle);
    if (!step || step.attribute !== "accessories" || !answer.values.length) continue;
    for (const value of answer.values) {
      const option = pickOptionForSize(step.options, value, desired.size);
      if (!option) continue;
      const mode = effectiveAccessoryMode(option);
      if (mode === "none") continue;
      if (normalizeToken(value).includes("ohne")) continue;
      desired.accessories.push({
        token: value,
        mode,
        linkedProductHandle: resolveLinkedProductHandle(option),
      });
    }
  }

  return desired;
};

const buildDetail = (product: ShopifyJsProduct, variant: ShopifyJsVariant, kinds: OptionKind[]): string => {
  const parts: string[] = [];
  for (const [index, kind] of kinds.entries()) {
    const value = variantOptionValue(variant, index).trim();
    if (!value || value === "Default Title") continue;
    if (kind === "size" || kind === "color" || kind === "material") parts.push(value);
    if (kind === "variante" && !normalizeToken(value).startsWith(normalizeToken(product.title.split("|")[0] ?? ""))) {
      // keep the informative tail ("mit Bettkasten") but skip pure base repeats
      const n = normalizeToken(value);
      if (!n.includes("ohne")) parts.push(value);
    }
  }
  return parts.join("  ·  ");
};

/**
 * Resolves the variant to sell for a recommended product.
 * bundleTokens: accessory answers with mode "bundle_variant" (encoded in the Variante option).
 */
export const resolveMainVariant = (product: ShopifyJsProduct, desired: DesiredSelection): ResolveOutcome => {
  const kinds = product.options.map((option) => classifyOptionName(optionName(option)));
  const otherCount = kinds.filter((kind) => kind === "other").length;

  // Multi-config products (e.g. premium Familienbett: Höhe + Einteilung) can't be
  // honestly resolved from the answers — PDP "Jetzt konfigurieren" instead of ATC.
  if (otherCount >= 2) {
    return { status: "configure", item: null };
  }

  const maxPrice = Math.max(...product.variants.map((variant) => variant.price));
  const varianteIndex = kinds.indexOf("variante");
  const bundleTokens = desired.accessories.filter((item) => item.mode === "bundle_variant").map((item) => item.token);
  const scoreBundleTokens = (varianteValue: string): number =>
    bundleTokens.filter((token) => matchesAccessoryToken(varianteValue, token)).length;

  const matchesDimensions = (variant: ShopifyJsVariant, ignoreColor = false): boolean => {
    for (const [index, kind] of kinds.entries()) {
      const value = variantOptionValue(variant, index);
      if (kind === "size" && desired.size) {
        if (normalizeSize(value) !== normalizeSize(desired.size)) return false;
      }
      if (kind === "color" && desired.color && !ignoreColor) {
        if (!colorTokensMatch(desired.color, value)) return false;
      }
      if (kind === "material" && desired.material) {
        // exact match — contains would let "Pur" satisfy a "pur_mesh" request
        if (normalizeToken(value) !== normalizeToken(desired.material)) return false;
      }
    }
    return true;
  };

  let pool = product.variants.filter((variant) => matchesDimensions(variant));
  if (!pool.length && desired.color) {
    const colorIndex = kinds.indexOf("color");
    const hasWantedColor =
      colorIndex >= 0 &&
      product.variants.some((variant) => colorTokensMatch(desired.color!, variantOptionValue(variant, colorIndex)));
    if (!hasWantedColor) return { status: "none", item: null };
    pool = product.variants.filter((variant) => matchesDimensions(variant, true));
  }

  if (varianteIndex >= 0) {
    // Bed must be a complete bed; addon_variant accessories (Etagenbett Schublade/Rausfallschutz)
    // resolve as separate set lines — never as the main variant.
    pool = pool.filter((variant) => isCompleteBed(variantOptionValue(variant, varianteIndex)));

    if (bundleTokens.length) {
      const ranked = [...pool].sort(
        (a, b) =>
          scoreBundleTokens(variantOptionValue(b, varianteIndex)) - scoreBundleTokens(variantOptionValue(a, varianteIndex)),
      );
      const best = ranked[0];
      const bestScore = best ? scoreBundleTokens(variantOptionValue(best, varianteIndex)) : 0;
      pool =
        bestScore > 0
          ? ranked.filter((variant) => scoreBundleTokens(variantOptionValue(variant, varianteIndex)) === bestScore)
          : pool;
    } else {
      const base = pool.filter((variant) => normalizeToken(variantOptionValue(variant, varianteIndex)).includes("ohne"));
      if (base.length) pool = base;
    }
  }

  if (!pool.length) return { status: "none", item: null };

  const colorIndex = kinds.indexOf("color");
  if (desired.color && colorIndex >= 0) {
    const wantNorm = normalizeToken(desired.color);
    pool.sort((a, b) => {
      const aRaw = normalizeToken(variantOptionValue(a, colorIndex));
      const bRaw = normalizeToken(variantOptionValue(b, colorIndex));
      const aExact = aRaw === wantNorm ? 1 : 0;
      const bExact = bRaw === wantNorm ? 1 : 0;
      return bExact - aExact;
    });
  }

  const pick = pool.find((variant) => variant.available) ?? pool[0];

  // Accessory-only leftovers vs the product max — complete beds (Nova ohne Zubehör
  // vs Bettkasten-Bundles) are already guarded by isCompleteBed.
  if (
    varianteIndex >= 0 &&
    !isCompleteBed(variantOptionValue(pick, varianteIndex)) &&
    pick.price < maxPrice * PRICE_SANITY_RATIO
  ) {
    console.error(`[ek-berater] price sanity failed for ${product.handle} variant ${pick.id} — refusing ATC`);
    return { status: "none", item: null };
  }

  const item: ResolvedItem = { product, variant: pick, detail: buildDetail(product, pick, kinds) };
  return { status: pick.available ? "ok" : "soldout", item };
};

/** When the flow has no size answer, inherit size from the resolved main bed variant. */
export const desiredSelectionWithMainSize = (
  product: ShopifyJsProduct,
  desired: DesiredSelection,
  mainVariant: ShopifyJsVariant | null | undefined,
): DesiredSelection => {
  if (desired.size || !mainVariant) return desired;
  const kinds = product.options.map((option) => classifyOptionName(optionName(option)));
  const sizeIndex = kinds.indexOf("size");
  if (sizeIndex < 0) return desired;
  const size = variantOptionValue(mainVariant, sizeIndex).trim();
  return size ? { ...desired, size } : desired;
};

/** Inherit size + resolved bed color/wood for accessory matching (separate products, previews). */
export const desiredSelectionWithMainVariant = (
  product: ShopifyJsProduct,
  desired: DesiredSelection,
  mainVariant: ShopifyJsVariant | null | undefined,
): DesiredSelection => {
  let result = desiredSelectionWithMainSize(product, desired, mainVariant);
  if (!mainVariant) return result;

  const kinds = product.options.map((option) => classifyOptionName(optionName(option)));
  const colorIndex = kinds.indexOf("color");
  if (colorIndex >= 0) {
    const colorRaw = variantOptionValue(mainVariant, colorIndex).trim();
    if (colorRaw && colorRaw !== "Default Title") {
      // Keep flow token (e.g. natur-weiss) — variantColorToToken would collapse to weiss.
      if (!result.color || !colorTokensMatch(result.color, colorRaw)) {
        const norm = normalizeToken(colorRaw);
        result = { ...result, color: norm === "naturweiss" ? "natur-weiss" : variantColorToToken(colorRaw) };
      }
    }
  }

  if (!result.wood) {
    const wood = inferWoodFromText(product.handle, product.title);
    if (wood) result = { ...result, wood };
  }

  return result;
};

const pickAccessoryVariant = (
  pool: ShopifyJsVariant[],
  kinds: OptionKind[],
  desired: DesiredSelection,
): ShopifyJsVariant => {
  const colorIndex = kinds.indexOf("color");
  const materialIndex = kinds.indexOf("material");

  const ranked = [...pool].sort((a, b) => {
    let scoreA = 0;
    let scoreB = 0;

    if (desired.color && colorIndex >= 0) {
      if (colorTokensMatch(desired.color, variantOptionValue(a, colorIndex))) scoreA += 2;
      if (colorTokensMatch(desired.color, variantOptionValue(b, colorIndex))) scoreB += 2;
    }

    if (desired.wood && materialIndex >= 0) {
      const aWood = variantOptionValue(a, materialIndex);
      const bWood = variantOptionValue(b, materialIndex);
      if (isWoodMaterialOption(aWood) && normalizeToken(aWood) === normalizeToken(desired.wood)) scoreA += 2;
      if (isWoodMaterialOption(bWood) && normalizeToken(bWood) === normalizeToken(desired.wood)) scoreB += 2;
    }

    return scoreB - scoreA;
  });

  return ranked.find((variant) => variant.available) ?? ranked[0];
};

/** Resolves an accessory line: addon variants live in the SAME product (Etagenbett),
 *  separate products (Matratze, Schublade) come with their own product JSON. */
export const resolveAccessoryItem = (
  product: ShopifyJsProduct,
  accessory: DesiredAccessory,
  desired: DesiredSelection,
  options?: { allowSoldOut?: boolean },
): ResolvedItem | null => {
  const kinds = product.options.map((option) => classifyOptionName(optionName(option)));
  const varianteIndex = kinds.indexOf("variante");

  const matchesAccessoryDimensions = (variant: ShopifyJsVariant, ignoreColor: boolean, ignoreWood: boolean): boolean => {
    for (const [index, kind] of kinds.entries()) {
      const value = variantOptionValue(variant, index);
      if (kind === "size" && desired.size) {
        if (normalizeSize(value) !== normalizeSize(desired.size)) return false;
      }
      if (kind === "color" && desired.color && !ignoreColor) {
        if (!colorTokensMatch(desired.color, value)) return false;
      }
      if (kind === "material") {
        if (desired.wood && isWoodMaterialOption(value) && !ignoreWood) {
          if (normalizeToken(value) !== normalizeToken(desired.wood)) return false;
        } else if (desired.material && !isWoodMaterialOption(value)) {
          if (normalizeToken(value) !== normalizeToken(desired.material)) return false;
        }
      }
    }
    return true;
  };

  let pool = product.variants.filter((variant) => matchesAccessoryDimensions(variant, false, false));
  if (!pool.length && desired.color) {
    pool = product.variants.filter((variant) => matchesAccessoryDimensions(variant, true, false));
  }
  if (!pool.length && desired.wood) {
    pool = product.variants.filter((variant) => matchesAccessoryDimensions(variant, false, true));
  }
  if (!pool.length && (desired.color || desired.wood)) {
    pool = product.variants.filter((variant) => matchesAccessoryDimensions(variant, true, true));
  }

  if (accessory.mode === "addon_variant" && varianteIndex >= 0) {
    pool = pool.filter((variant) => {
      const value = variantOptionValue(variant, varianteIndex);
      return matchesAccessoryToken(value, accessory.token) && !isCompleteBed(value);
    });
  }

  // Size filter may legitimately not apply (accessory without size option) — but if it
  // emptied the pool while the product does have sizes, drop nothing silently: no item.
  if (!pool.length) return null;

  const pick = pickAccessoryVariant(pool, kinds, desired);
  if (!pick.available && !options?.allowSoldOut) return null;

  return { product, variant: pick, detail: buildDetail(product, pick, kinds) };
};

/** True when the chosen main variant already covers the bundle token in its
 *  Variante value (e.g. "Hausbett mit Bettkasten" covers "bettkasten"). */
export const isBundleTokenInVariant = (product: ShopifyJsProduct, variant: ShopifyJsVariant, token: string): boolean => {
  const kinds = product.options.map((option) => classifyOptionName(optionName(option)));
  const varianteIndex = kinds.indexOf("variante");
  if (varianteIndex < 0) return false;
  return matchesAccessoryToken(variantOptionValue(variant, varianteIndex), token);
};

/** Human-readable subtitle for the winner card, e.g. "Kiefer Natur  |  90 × 200" */
export const buildSubtitle = (product: ShopifyJsProduct, variant: ShopifyJsVariant): string => {
  const kinds = product.options.map((option) => classifyOptionName(optionName(option)));
  const parts: string[] = [];
  const colorIndex = kinds.indexOf("color");
  const sizeIndex = kinds.indexOf("size");
  const materialIndex = kinds.indexOf("material");
  if (colorIndex >= 0) parts.push(variantOptionValue(variant, colorIndex));
  if (materialIndex >= 0) parts.push(variantOptionValue(variant, materialIndex));
  if (sizeIndex >= 0) parts.push(variantOptionValue(variant, sizeIndex));
  return parts
    .map((part) => part.trim())
    .filter((part) => part && part !== "Default Title")
    .join("  |  ");
};

// ponytail: Ajax API only fills option1–3; Variante lives in variant.options[3]
{
  const fourOptionProduct: ShopifyJsProduct = {
    id: 1,
    title: "Jugendbett Nova Standard",
    handle: "jugendbett-nova-standard-natur",
    url: "/products/jugendbett-nova-standard-natur",
    options: ["Art", "Farbe", "Größe", "Variante"],
    variants: [
      {
        id: 11,
        title: "Standard / Natur / 90x200 / Jugendbett ohne Zubehör",
        option1: "Standard",
        option2: "Natur",
        option3: "90x200",
        options: ["Standard", "Natur", "90x200", "Jugendbett ohne Zubehör"],
        sku: "ohne",
        price: 19990,
        compare_at_price: null,
        available: true,
      },
      {
        id: 12,
        title: "Standard / Natur / 90x200 / Jugendbett mit Bettkasten",
        option1: "Standard",
        option2: "Natur",
        option3: "90x200",
        options: ["Standard", "Natur", "90x200", "Jugendbett mit Bettkasten"],
        sku: "kasten",
        price: 41990,
        compare_at_price: null,
        available: true,
      },
    ],
  };
  const outcome = resolveMainVariant(fourOptionProduct, {
    size: "90x200",
    color: "natur",
    wood: "buche",
    material: null,
    accessories: [],
  });
  if (outcome.status !== "ok" || outcome.item?.variant.id !== 11) {
    throw new Error("[ek-berater] variantResolver self-check failed (4-option ohne Zubehör)");
  }
}
